import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import CloseIcon from "@mui/icons-material/Close";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import {
  updateCartQuantity,
  removeCartItem,
} from "actions/dtfCart";
import {
  buildCartItemWithQuantity,
  getArtworkOptionLabels,
  getDtfCartItemTitle,
} from "pages/dtf-transfers/by-size/helpers";
import { SHEET_SIZES } from "pages/dtf-transfers/gang-sheet/data";
import { getSheetUnitPrice } from "pages/dtf-transfers/gang-sheet/helpers";
import GangSheetBuilder from "pages/dtf-transfers/gang-sheet/GangSheetBuilder";
import {
  CartItemCard,
  CartItemImage,
  CartItemBody,
  CartItemTitle,
  CartItemLine,
  CartItemLink,
  CartItemOptions,
  CartItemOption,
  CartItemActions,
  CartItemActionsTop,
  QuantityControl,
  QuantityInput,
  CartItemPricing,
  UnitPrice,
  LineTotal,
  RemoveButton,
  EditButton,
} from "styles/pages/dtf-transfers/cart";

const CartItemCardComponent = ({ item }) => {
  const dispatch = useDispatch();
  const emailid = useSelector((state) => state?.auth?.profile?.emailid);
  const token = useSelector((state) => state?.auth?.profile?.token);
  const isCartLoading = useSelector((state) => state?.dtfCart?.loading);

  const primarySize = item.sizes?.[0];
  const quantity = primarySize?.quantity ?? 1;
  const [quantityInput, setQuantityInput] = useState(String(quantity));
  const unitPrice = primarySize?.unitPrice ?? item.total ?? 0;
  const sizeLabel = primarySize
    ? `${primarySize.width} in x ${primarySize.height} in`
    : "—";
  const optionLabels = getArtworkOptionLabels(item);
  const canEditGangSheet =
    item.productType === "gang-sheet" &&
    (item.mode === "create" || Boolean(item.canvasData));

  const [editOpen, setEditOpen] = useState(false);
  const [editSheetWidth, setEditSheetWidth] = useState(
    () => Number(item.sheetWidth || primarySize?.width) || 22,
  );
  const [editSheetHeight, setEditSheetHeight] = useState(
    () => Number(item.sheetHeight || primarySize?.height) || 20,
  );

  const cartIdentifiers = {
    cartId: item.cartId,
    cartRecordId: item.cartRecordId ?? item.id,
  };

  useEffect(() => {
    setQuantityInput(String(quantity));
  }, [quantity]);

  const handleQuantityChange = async (nextQuantity) => {
    if (!emailid || isCartLoading || nextQuantity < 1) return;

    await dispatch(
      updateCartQuantity(emailid, token, {
        cartId: item.cartId,
        item: buildCartItemWithQuantity(item, nextQuantity),
      }),
    );
  };

  const commitQuantity = () => {
    const nextQuantity = Math.max(1, parseInt(quantityInput, 10) || 1);
    setQuantityInput(String(nextQuantity));
    if (nextQuantity !== quantity) {
      handleQuantityChange(nextQuantity);
    }
  };

  const handleRemove = async () => {
    if (!emailid || isCartLoading) return;

    await dispatch(removeCartItem(emailid, token, cartIdentifiers));
  };

  const openEditDialog = () => {
    setEditSheetWidth(Number(item.sheetWidth || primarySize?.width) || 22);
    setEditSheetHeight(Number(item.sheetHeight || primarySize?.height) || 20);
    setEditOpen(true);
  };

  return (
    <>
      <CartItemCard>
        <CartItemImage
          src={item.previewUrl}
          alt={item.fileName || "Cart item"}
        />

        <CartItemBody>
          <CartItemTitle>{getDtfCartItemTitle(item)}</CartItemTitle>
          <CartItemLine>
            Upload:{" "}
            <CartItemLink
              href={item.previewUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              {item.fileName}
            </CartItemLink>
          </CartItemLine>
          <CartItemLine>Size: {sizeLabel}</CartItemLine>
          {optionLabels.length ? (
            <CartItemOptions>
              {optionLabels.map((label) => (
                <CartItemOption key={label}>
                  <CheckCircleIcon />
                  {label}
                </CartItemOption>
              ))}
            </CartItemOptions>
          ) : null}
        </CartItemBody>

        <CartItemActions>
          <CartItemActionsTop>
            <QuantityControl>
              <QuantityInput
                type="number"
                min="1"
                step="1"
                aria-label="Quantity"
                disabled={isCartLoading}
                value={quantityInput}
                onChange={(event) => setQuantityInput(event.target.value)}
                onBlur={commitQuantity}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.currentTarget.blur();
                  }
                }}
              />
            </QuantityControl>
            {canEditGangSheet ? (
              <EditButton
                type="button"
                aria-label="Edit gang sheet"
                disabled={isCartLoading}
                onClick={openEditDialog}
              >
                <EditOutlinedIcon fontSize="small" />
              </EditButton>
            ) : null}
            <RemoveButton
              type="button"
              aria-label="Remove item"
              disabled={isCartLoading}
              onClick={handleRemove}
            >
              <CloseIcon fontSize="small" />
            </RemoveButton>
          </CartItemActionsTop>

          <CartItemPricing>
            <UnitPrice>${unitPrice.toFixed(2)} ea</UnitPrice>
            <LineTotal>${(item.total || 0).toFixed(2)}</LineTotal>
          </CartItemPricing>
        </CartItemActions>
      </CartItemCard>

      {canEditGangSheet ? (
        <Dialog
          open={editOpen}
          onClose={() => setEditOpen(false)}
          fullScreen
        >
          <DialogTitle
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "16px 24px",
              borderBottom: "1px solid #e5e7eb",
              backgroundColor: "#f8fafc",
              fontFamily: "Inter600",
              fontSize: "20px",
              color: "#1e293b",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <span
                style={{
                  fontSize: "16px",
                  color: "#64748b",
                  fontFamily: "Inter500",
                }}
              >
                Edit Gang Sheet:
              </span>
              <select
                value={`${editSheetWidth}x${editSheetHeight}`}
                onChange={(e) => {
                  const selected = SHEET_SIZES.find(
                    (s) => s.id === e.target.value,
                  );
                  if (selected) {
                    setEditSheetWidth(selected.width);
                    setEditSheetHeight(selected.height);
                  }
                }}
                style={{
                  padding: "6px 12px",
                  borderRadius: "6px",
                  border: "1px solid #cbd5e1",
                  fontFamily: "Inter600",
                  fontSize: "15px",
                  color: "#1e293b",
                  backgroundColor: "#ffffff",
                  cursor: "pointer",
                  outline: "none",
                }}
              >
                {SHEET_SIZES.map((size) => {
                  const price = getSheetUnitPrice(size.width, size.height);
                  return (
                    <option key={size.id} value={size.id}>
                      {size.width}&quot; × {size.height}&quot; — $
                      {price.toFixed(2)}
                    </option>
                  );
                })}
              </select>
            </div>
            <IconButton onClick={() => setEditOpen(false)} size="large">
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent style={{ padding: "24px", backgroundColor: "#f8fafc" }}>
            <GangSheetBuilder
              key={`edit-${item.cartId || item.id}-${editSheetWidth}x${editSheetHeight}`}
              sheetWidth={editSheetWidth}
              sheetHeight={editSheetHeight}
              editItem={item}
              onClose={() => setEditOpen(false)}
            />
          </DialogContent>
        </Dialog>
      ) : null}
    </>
  );
};

export default CartItemCardComponent;
